import React, { useMemo, useRef, useState } from "react";
import { Plus, Trash2, ChevronRight, ChevronDown, CornerDownRight, X, Link2, AlertTriangle, Anchor, Trash } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { pct, fK, todayISO, toast } from "../../lib/utils";
import { api } from "../../lib/api";

// ─── Dependency helpers (Finish-to-Start, Start-to-Start, Finish-to-Finish, Start-to-Finish + lag) ──
// Task predecessor shape: { id: "T2", type: "FS"|"SS"|"FF"|"SF", lag: <int days, may be negative> }
const DEP_TYPES = ["FS", "SS", "FF", "SF"];
const DEP_LABELS = { FS: "Finish → Start", SS: "Start → Start", FF: "Finish → Finish", SF: "Start → Finish" };

function _parseISO(s) { return s ? new Date(s + "T00:00:00Z") : null; }
function _fmtISO(d) { return d.toISOString().slice(0, 10); }
function _addDays(d, n) { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; }
function _duration(t) {
  const s = _parseISO(t.start), e = _parseISO(t.end);
  if (!s || !e) return 0;
  return Math.max(0, Math.round((e - s) / 86400000));
}

/** Given a list of tasks (with .predecessors), returns a new list where each
 *  task's start/end has been shifted forward so all dependency constraints are
 *  satisfied. Preserves task durations. Iterates topologically until stable
 *  (max N passes) — safe against cycles. */
function cascadeSchedule(tasks) {
  const byId = new Map(tasks.map(t => [t.id, { ...t }]));
  const N = tasks.length;
  for (let pass = 0; pass < N + 1; pass++) {
    let moved = false;
    for (const t of byId.values()) {
      const preds = t.predecessors || [];
      if (!preds.length) continue;
      const dur = _duration(t);
      let minStart = _parseISO(t.start);
      let minEnd = _parseISO(t.end);
      for (const p of preds) {
        const parent = byId.get(p.id);
        if (!parent) continue;
        const ps = _parseISO(parent.start), pe = _parseISO(parent.end);
        const lag = Number(p.lag || 0);
        let reqStart = null, reqEnd = null;
        if (p.type === "FS") reqStart = _addDays(pe, 1 + lag);
        else if (p.type === "SS") reqStart = _addDays(ps, lag);
        else if (p.type === "FF") reqEnd = _addDays(pe, lag);
        else if (p.type === "SF") reqEnd = _addDays(ps, lag);
        if (reqStart && (!minStart || reqStart > minStart)) minStart = reqStart;
        if (reqEnd && (!minEnd || reqEnd > minEnd)) minEnd = reqEnd;
      }
      // Duration-preserving shift.
      let newStart = minStart, newEnd = minEnd;
      if (minStart && (!minEnd || _addDays(minStart, dur) > minEnd)) newEnd = _addDays(minStart, dur);
      if (minEnd && (!minStart || _addDays(minEnd, -dur) > minStart)) newStart = _addDays(minEnd, -dur);
      const ns = newStart ? _fmtISO(newStart) : t.start;
      const ne = newEnd ? _fmtISO(newEnd) : t.end;
      if (ns !== t.start || ne !== t.end) {
        t.start = ns; t.end = ne; moved = true;
      }
    }
    if (!moved) break;
  }
  // Preserve original task order.
  return tasks.map(t => byId.get(t.id) || t);
}

export default function Tracker() {
  const { doc, mutate, readOnly, replaceDoc } = useProject();
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [baselineBusy, setBaselineBusy] = useState(false);

  const statuses = ["Completed", "In Review", "In Progress", "Not Started"];
  const statusColor = { "Completed": "#22C55E", "In Review": "#A855F7", "In Progress": "#3B82F6", "Not Started": "#71717A" };

  const reqByCat = {};
  doc.requirements.forEach(r => {
    if (!reqByCat[r.category]) reqByCat[r.category] = { total: 0, done: 0 };
    reqByCat[r.category].total++;
    if (r.status === "Completed") reqByCat[r.category].done++;
  });

  const avg = (arr, key) => arr.length ? Math.round(arr.reduce((s, x) => s + (x[key] || 0), 0) / arr.length) : 0;
  const reqDone = doc.requirements.filter(r => r.status === "Completed").length;
  const actDone = doc.actions.filter(a => a.status === "Completed").length;

  const actionGroups = [
    ["Completed",   "#22C55E", doc.actions.filter(a => a.status === "Completed")],
    ["In Progress", "#3B82F6", doc.actions.filter(a => a.status === "In Progress")],
    ["Not Started", "#71717A", doc.actions.filter(a => a.status === "Not Started")],
    ["Overdue",     "#EF4444", doc.actions.filter(a => a.status === "Overdue")],
  ];

  const updateTask = (id, key, value) => {
    mutate(d => {
      const next = { ...d, tasks: d.tasks.map(t => t.id === id ? { ...t, [key]: value } : t) };
      // Auto-cascade when a schedule-affecting field changes.
      if (key === "start" || key === "end" || key === "predecessors") {
        next.tasks = cascadeSchedule(next.tasks);
      }
      return next;
    });
  };
  const removeTask = (id) => {
    if (!window.confirm("Delete this task?")) return;
    mutate(d => ({
      ...d,
      // Also strip any predecessor links pointing at the deleted task.
      tasks: d.tasks
        .filter(t => t.id !== id)
        .map(t => t.predecessors?.length
          ? { ...t, predecessors: t.predecessors.filter(p => p.id !== id) }
          : t),
    }));
  };

  // ── Baseline (single active snapshot) ─────────────────────────────────
  const baseline = doc.baseline || null;
  const baselineIndex = useMemo(() => {
    if (!baseline) return { tasks: {}, milestones: {} };
    const t = {}; (baseline.tasks || []).forEach(x => { if (x.id) t[x.id] = x; });
    const m = {}; (baseline.milestones || []).forEach(x => { const k = x.id || x.name; if (k) m[k] = x; });
    return { tasks: t, milestones: m };
  }, [baseline]);
  const taskVariance = (t) => {
    const b = baselineIndex.tasks[t.id];
    if (!b || !b.end || !t.end) return null;
    const diff = Math.round((_parseISO(t.end) - _parseISO(b.end)) / 86400000);
    return diff;
  };
  const saveBaseline = async () => {
    if (baseline && !window.confirm("Overwrite the existing baseline snapshot?")) return;
    setBaselineBusy(true);
    try {
      // Pass current in-memory tasks & milestones so the snapshot captures
      // dates the user just edited, even if the debounced auto-save hasn't
      // flushed yet.
      const fresh = await api.saveBaseline(doc.id, undefined, doc.tasks, doc.milestones);
      replaceDoc({ ...doc, baseline: fresh });
      toast(`Baseline saved · ${fresh.tasks?.length || 0} tasks · ${fresh.milestones?.length || 0} milestones`);
    } catch (e) {
      toast("Baseline save failed: " + (e?.response?.data?.detail || e.message || "unknown"));
    } finally { setBaselineBusy(false); }
  };
  const clearBaseline = async () => {
    if (!window.confirm("Clear the current baseline? Variance chips will disappear.")) return;
    setBaselineBusy(true);
    try {
      await api.clearBaseline(doc.id);
      const next = { ...doc }; delete next.baseline;
      replaceDoc(next);
      toast("Baseline cleared");
    } catch (e) {
      toast("Baseline clear failed: " + (e?.response?.data?.detail || e.message || "unknown"));
    } finally { setBaselineBusy(false); }
  };

  // ── Subtasks ──────────────────────────────────────────────────────────
  const toggleRow = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const addSubtask = (taskId) => {
    const n = (doc.tasks.find(t => t.id === taskId)?.subtasks?.length || 0) + 1;
    const sub = { id: `${taskId}-S${n}_${Date.now().toString(36).slice(-3)}`, name: "New subtask", pct: 0, status: "Not Started", owner: "" };
    mutate(d => ({
      ...d, tasks: d.tasks.map(t => t.id === taskId
        ? { ...t, subtasks: [...(t.subtasks || []), sub] }
        : t),
    }));
    setExpanded(prev => new Set(prev).add(taskId));
  };
  const updateSubtask = (taskId, subId, key, value) => {
    mutate(d => ({
      ...d, tasks: d.tasks.map(t => t.id !== taskId ? t : {
        ...t, subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, [key]: value } : s),
      }),
    }));
  };
  const removeSubtask = (taskId, subId) => {
    if (!window.confirm("Delete this subtask?")) return;
    mutate(d => ({
      ...d, tasks: d.tasks.map(t => t.id !== taskId ? t : {
        ...t, subtasks: (t.subtasks || []).filter(s => s.id !== subId),
      }),
    }));
  };
  const subAvg = (subs) => {
    if (!subs?.length) return null;
    return Math.round(subs.reduce((s, x) => s + (x.pct || 0), 0) / subs.length);
  };

  // Effective progress per task: rolls up from subtasks when present, falls back to task.pct
  const effectivePct = (t) => {
    const a = subAvg(t.subtasks);
    return a == null ? (t.pct || 0) : a;
  };
  const tasksWithEff = doc.tasks.map(t => ({ ...t, _eff: effectivePct(t) }));
  const avgPct  = avg(tasksWithEff, "_eff");
  const critPct = avg(tasksWithEff.filter(t => t.critical), "_eff");

  // Toggle one dependency on/off for a subtask
  const toggleDep = (taskId, subId, depId) => {
    mutate(d => ({
      ...d, tasks: d.tasks.map(t => t.id !== taskId ? t : {
        ...t, subtasks: (t.subtasks || []).map(s => {
          if (s.id !== subId) return s;
          const cur = new Set(s.dependsOn || []);
          if (cur.has(depId)) cur.delete(depId); else cur.add(depId);
          return { ...s, dependsOn: Array.from(cur) };
        }),
      }),
    }));
  };

  const isSubDone = (s) => s.status === "Completed" || (s.pct || 0) >= 100;
  const isBlocked = (subs, s) =>
    (s.dependsOn || []).some(depId => {
      const dep = subs.find(x => x.id === depId);
      return dep && !isSubDone(dep);
    });

  const addTask = () => {
    const name = document.getElementById("nt-name")?.value?.trim();
    if (!name) return toast("Task name required");
    const start = document.getElementById("nt-start")?.value || todayISO();
    const end   = document.getElementById("nt-end")?.value   || todayISO();
    const pctV  = Number(document.getElementById("nt-pct")?.value) || 0;
    const crit  = document.getElementById("nt-crit")?.checked || false;
    const n = doc.tasks.length + 1;
    mutate(d => ({
      ...d, tasks: [...d.tasks, {
        id: `T${n}_${Date.now().toString(36).slice(-4)}`, name, start, end, pct: pctV, critical: crit, track: n % 3,
      }],
    }));
    setShowAdd(false);
  };

  // Risk-to-Schedule index — mirrors backend compute_schedule_risk_index().
  // Maps schedule item id → {count, maxSeverity, color, risks[]}.
  const riskIndex = useMemo(() => {
    const RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
    const COLOR = { Critical: "#DC2626", High: "#F97316", Medium: "#F59E0B", Low: "#22C55E" };
    const idx = {};
    for (const r of (doc.raids || [])) {
      const rtype = (r.type || "").toLowerCase();
      const rstat = (r.status || "").toLowerCase();
      if (!["risk", "issue"].includes(rtype)) continue;
      if (["closed", "resolved"].includes(rstat)) continue;
      const sev = r.score || r.impact || "Medium";
      for (const sid of (r.linkedScheduleIds || [])) {
        if (!sid) continue;
        const e = idx[sid] || { count: 0, maxRank: 0, maxSeverity: "", color: COLOR.Medium, risks: [] };
        e.count += 1;
        e.risks.push({ ref: r.ref, title: r.title, severity: sev });
        const rank = RANK[sev] || 0;
        if (rank > e.maxRank) { e.maxRank = rank; e.maxSeverity = sev; e.color = COLOR[sev] || COLOR.Medium; }
        idx[sid] = e;
      }
    }
    return idx;
  }, [doc.raids]);

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Live View</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Graphical Progress Tracker</h2>
          {baseline && (
            <div className="font-mono text-[10px] text-zinc-500 mt-1" data-testid="baseline-meta">
              Baseline · {baseline.name || "unnamed"} · frozen {(baseline.savedAt || "").slice(0, 10)}
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            {baseline ? (
              <>
                <button className="btn btn-secondary btn-sm" onClick={saveBaseline} disabled={baselineBusy} data-testid="btn-save-baseline" title="Overwrite the current baseline with today's dates">
                  <Anchor size={12}/> Re-baseline
                </button>
                <button className="btn btn-ghost btn-sm" onClick={clearBaseline} disabled={baselineBusy} data-testid="btn-clear-baseline" title="Clear baseline">
                  <Trash size={12}/> Clear
                </button>
              </>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={saveBaseline} disabled={baselineBusy} data-testid="btn-save-baseline" title="Freeze current task & milestone dates as the baseline">
                <Anchor size={12}/> Save Baseline
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)} data-testid="btn-add-task">
              <Plus size={12}/> Add Task
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Tile label="Overall Progress"  value={`${avgPct}%`}  sub={`Across ${doc.tasks.length} tasks`}  color="#22C55E" />
        <Tile label="Critical Path"     value={`${critPct}%`} sub={`${doc.tasks.filter(t=>t.critical).length} critical tasks`} color="#EF4444" />
        <Tile label="Req. Completion"   value={`${pct(reqDone, doc.requirements.length)}%`} sub={`${reqDone} of ${doc.requirements.length} done`} color="#3B82F6" />
        <Tile label="Actions Done"      value={`${pct(actDone, doc.actions.length)}%`} sub={`${actDone} of ${doc.actions.length}`} color="#F59E0B" />
      </div>

      {/* Editable Tasks */}
      <div className="card p-5 mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">Tasks — click any field to edit · click ▸ to expand subtasks</div>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr>
              <th style={{ width: 28 }}></th><th>ID</th><th>Name</th><th>Start</th><th>End</th><th>%</th><th>Critical</th><th style={{ width: 180 }}>Predecessors</th><th>Subs</th>{!readOnly && <th></th>}
            </tr></thead>
            <tbody>
              {doc.tasks.map(t => {
                const isOpen = expanded.has(t.id);
                const subs = t.subtasks || [];
                const avg = subAvg(subs);
                return (
                  <React.Fragment key={t.id}>
                    <tr>
                      <td className="text-zinc-400">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleRow(t.id)} data-testid={`btn-expand-task-${t.id}`} title={isOpen ? "Collapse subtasks" : "Expand subtasks"}>
                          {isOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                        </button>
                      </td>
                      <td className="font-mono text-xs text-zinc-500">{t.id}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <input className="fld flex-1" defaultValue={t.name} readOnly={readOnly}
                                 onBlur={e => e.target.value !== t.name && updateTask(t.id, "name", e.target.value)}
                                 data-testid={`task-name-${t.id}`}/>
                          {riskIndex[t.id] && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-semibold text-white shrink-0"
                              style={{ background: riskIndex[t.id].color }}
                              title={`${riskIndex[t.id].count} open risk(s) linked · max ${riskIndex[t.id].maxSeverity}`}
                              data-testid={`task-risk-flag-${t.id}`}
                            >
                              <AlertTriangle size={10} />
                              {riskIndex[t.id].count > 1 ? riskIndex[t.id].count : ""}
                            </span>
                          )}
                          {(() => {
                            const v = taskVariance(t);
                            if (v === null || v === 0) return null;
                            const color = v > 5 ? "#DC2626" : v > 0 ? "#F59E0B" : "#16A34A";
                            const label = v > 0 ? `+${v}d` : `${v}d`;
                            const title = `Baseline end ${baselineIndex.tasks[t.id]?.end} → current ${t.end}`;
                            return (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono font-semibold text-white shrink-0"
                                style={{ background: color }}
                                title={title}
                                data-testid={`task-variance-${t.id}`}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td><input key={`s-${t.id}-${t.start}`} type="date" className="fld" defaultValue={t.start} readOnly={readOnly}
                                 onBlur={e => updateTask(t.id, "start", e.target.value)}
                                 data-testid={`task-start-${t.id}`}/></td>
                      <td><input key={`e-${t.id}-${t.end}`} type="date" className="fld" defaultValue={t.end} readOnly={readOnly}
                                 onBlur={e => updateTask(t.id, "end", e.target.value)}
                                 data-testid={`task-end-${t.id}`}/></td>
                      <td><input type="number" min={0} max={100} className="fld w-20" defaultValue={t.pct || 0} readOnly={readOnly}
                                 onBlur={e => updateTask(t.id, "pct", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                                 data-testid={`task-pct-${t.id}`}/></td>
                      <td>
                        <input type="checkbox" defaultChecked={!!t.critical} disabled={readOnly}
                               onChange={e => updateTask(t.id, "critical", e.target.checked)}
                               data-testid={`task-crit-${t.id}`}/>
                      </td>
                      <td>
                        <PredecessorPicker
                          task={t}
                          siblings={doc.tasks}
                          readOnly={readOnly}
                          onChange={(next) => updateTask(t.id, "predecessors", next)}
                        />
                      </td>
                      <td className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                        {subs.length > 0
                          ? <span title={`Subtasks avg: ${avg}%`}>{subs.length} · <span className="text-zinc-700 font-semibold">{avg}%</span></span>
                          : <span className="text-zinc-400">—</span>}
                      </td>
                      {!readOnly && (
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => removeTask(t.id)} title="Delete" data-testid={`btn-del-task-${t.id}`}>
                            <Trash2 size={11}/>
                          </button>
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr data-testid={`subtasks-row-${t.id}`}>
                        <td colSpan={!readOnly ? 10 : 9} className="bg-[var(--surface)] p-0">
                          <div className="px-4 py-3 border-l-2 border-[var(--navy)]">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1.5">
                                <CornerDownRight size={11}/> Subtasks of <span className="text-zinc-700 font-semibold">{t.name}</span>
                              </div>
                              {!readOnly && (
                                <button className="btn btn-secondary btn-sm" onClick={() => addSubtask(t.id)} data-testid={`btn-add-subtask-${t.id}`}>
                                  <Plus size={11}/> Add Subtask
                                </button>
                              )}
                            </div>
                            {subs.length === 0 ? (
                              <div className="text-xs text-zinc-500 italic py-3 text-center">No subtasks yet. Click "Add Subtask" to break this down.</div>
                            ) : (
                              <table className="tbl">
                                <thead><tr>
                                  <th style={{ width: 110 }}>ID</th>
                                  <th>Name</th>
                                  <th style={{ width: 140 }}>Owner</th>
                                  <th style={{ width: 170 }}>Depends on</th>
                                  <th style={{ width: 100 }}>%</th>
                                  <th style={{ width: 140 }}>Status</th>
                                  {!readOnly && <th style={{ width: 30 }}></th>}
                                </tr></thead>
                                <tbody>
                                  {subs.map(s => {
                                    const blocked = isBlocked(subs, s);
                                    return (
                                    <tr key={s.id} className={blocked ? "bg-amber-50/40" : ""}>
                                      <td className="font-mono text-[10px] text-zinc-500 truncate">{s.id}</td>
                                      <td>
                                        <div className="flex items-center gap-1.5">
                                          {blocked && (
                                            <span title="Blocked — an upstream subtask isn't complete yet" data-testid={`subtask-blocked-${s.id}`}>
                                              <AlertTriangle size={11} className="text-amber-600 shrink-0"/>
                                            </span>
                                          )}
                                          <input className="fld" defaultValue={s.name} readOnly={readOnly}
                                                 onBlur={e => e.target.value !== s.name && updateSubtask(t.id, s.id, "name", e.target.value)}
                                                 data-testid={`subtask-name-${s.id}`}/>
                                        </div>
                                      </td>
                                      <td>
                                        <input className="fld" defaultValue={s.owner || ""} readOnly={readOnly} placeholder="—"
                                               onBlur={e => updateSubtask(t.id, s.id, "owner", e.target.value)}/>
                                      </td>
                                      <td>
                                        <DepsPicker
                                          sub={s} siblings={subs}
                                          readOnly={readOnly}
                                          onToggle={(depId) => toggleDep(t.id, s.id, depId)}
                                        />
                                      </td>
                                      <td>
                                        <input type="number" min={0} max={100} className="fld w-20" defaultValue={s.pct || 0} readOnly={readOnly}
                                               onBlur={e => updateSubtask(t.id, s.id, "pct", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                                               data-testid={`subtask-pct-${s.id}`}/>
                                      </td>
                                      <td>
                                        <select className="fld" defaultValue={s.status || "Not Started"} disabled={readOnly}
                                                onChange={e => updateSubtask(t.id, s.id, "status", e.target.value)}
                                                data-testid={`subtask-status-${s.id}`}>
                                          {statuses.map(o => <option key={o}>{o}</option>)}
                                        </select>
                                      </td>
                                      {!readOnly && (
                                        <td>
                                          <button className="btn btn-ghost btn-sm" onClick={() => removeSubtask(t.id, s.id)} title="Delete subtask" data-testid={`btn-del-subtask-${s.id}`}>
                                            <Trash2 size={11}/>
                                          </button>
                                        </td>
                                      )}
                                    </tr>);
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task Completion Bars */}
      <div className="card p-5 mb-5" data-export-capture="tracker-task-completion" data-export-title="Tracker · Task Completion by Phase">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Task completion by phase</div>
        <div className="font-mono text-[10px] text-zinc-400 mb-4">Bars reflect live progress — when subtasks exist, the parent rolls up to their average automatically.</div>
        <div className="space-y-2.5">
          {tasksWithEff.map(t => {
            const p = t._eff;
            const rolled = (t.subtasks?.length || 0) > 0;
            return (
            <div key={t.id} className="grid grid-cols-[180px_1fr_110px] gap-3 items-center">
              <div className="font-mono text-[11px] truncate" title={t.name}>
                <span className={t.critical ? "text-red-700" : "text-zinc-500"}>{t.critical ? "▸ " : ""}</span>
                <span className="text-zinc-700">{t.name}</span>
                {rolled && <span className="ml-1 text-zinc-400" title={`Rolled up from ${t.subtasks.length} subtasks`}>· rollup</span>}
              </div>
              <div className="relative h-5 bg-zinc-100 rounded-sm overflow-hidden" data-testid={`phase-bar-${t.id}`}>
                <div className="h-full bar-fill" style={{ width: `${p}%`, background: t.critical ? "rgba(239,68,68,.55)" : "rgba(59,130,246,.55)" }}/>
                {p > 12 && (
                  <span className="absolute inset-0 flex items-center px-2 font-mono text-[10px]"
                        style={{ color: t.critical ? "#b91c1c" : "#1d4ed8" }}>{p}%</span>
                )}
                {p === 0 && <span className="absolute inset-0 flex items-center px-2 font-mono text-[10px] text-zinc-500">Not started</span>}
              </div>
              <div className="font-mono text-[10px] text-zinc-500 text-right">{t.start.slice(5)} → {t.end.slice(5)}</div>
            </div>
          );})}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <div className="card p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-4">Requirements by Status</div>
          {statuses.map(s => {
            const cnt = doc.requirements.filter(r => r.status === s).length;
            const p = pct(cnt, doc.requirements.length);
            return (
              <div key={s} className="mb-3 last:mb-0">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-500">{s}</span>
                  <span className="font-mono text-zinc-700">{cnt} <span className="text-zinc-500">({p}%)</span></span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bar-fill" style={{ width: `${p}%`, background: statusColor[s] }}/>
                </div>
              </div>
            );
          })}
        </div>
        <div className="card p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-4">Requirements by Category</div>
          {Object.entries(reqByCat).map(([cat, data]) => {
            const p = pct(data.done, data.total);
            return (
              <div key={cat} className="mb-3 last:mb-0">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-500">{cat}</span>
                  <span className="font-mono text-zinc-700">{data.done}/{data.total}</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bar-fill" style={{ width: `${p}%`, background: "#A855F7" }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Budget burn */}
      <div className="card p-5 mb-5" data-export-capture="tracker-budget-burn" data-export-title="Tracker · Budget Burn by Category">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-4">Budget Burn by Category</div>
        <div className="space-y-3">
          {doc.budget.map(b => {
            const sp = pct(b.actual, b.allocated);
            const fp = pct(b.forecast, b.allocated);
            const over = b.forecast > b.allocated;
            return (
              <div key={b.category} className="grid grid-cols-[180px_1fr] gap-3 items-center">
                <div className="text-xs truncate text-zinc-700">{b.category}</div>
                <div>
                  <div className="relative h-5 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bar-fill" style={{ width: `${Math.min(fp,100)}%`, background: over ? "rgba(239,68,68,.18)" : "rgba(245,158,11,.18)" }}/>
                    <div className="absolute inset-y-0 left-0 bar-fill" style={{ width: `${Math.min(sp,100)}%`, background: over && sp > 100 ? "rgba(239,68,68,.55)" : "rgba(245,158,11,.55)" }}/>
                    {sp > 14 && <span className="absolute inset-y-0 left-2 flex items-center font-mono text-[10px] text-amber-800 font-medium">{sp}%</span>}
                  </div>
                  <div className="flex gap-4 mt-1 font-mono text-[10px]">
                    <span className="text-zinc-500">Budget: {fK(b.allocated)}</span>
                    <span className="text-amber-700">Spent: {fK(b.actual)}</span>
                    <span className={b.forecast <= b.allocated ? "text-green-700" : "text-red-700"}>Forecast: {fK(b.forecast)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Heatmap */}
      <div className="card p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-4">Action items status</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {actionGroups.map(([label, color, items]) => (
            <div key={label} className="rounded-sm border p-3" style={{ background: color + "0F", borderColor: color + "33" }}>
              <div className="font-display text-2xl font-extrabold leading-none" style={{ color }}>{items.length}</div>
              <div className="font-mono text-[10px] mt-1 uppercase tracking-[0.12em]" style={{ color }}>{label}</div>
              {items.slice(0, 2).map((a, i) => (
                <div key={i} className="text-[11px] text-zinc-500 truncate mt-2 pt-2 border-t" style={{ borderColor: color + "22" }}>{a.title}</div>
              ))}
              {items.length > 2 && <div className="text-[10px] text-zinc-500 mt-1">+{items.length - 2} more</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAdd && (
        <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div className="modal max-w-md anim-in">
            <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
              <div className="font-display text-lg font-bold tracking-tight text-zinc-900">Add Task</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="fld-lbl">Task name</label>
                <input id="nt-name" className="fld" placeholder="e.g. Integration testing" data-testid="input-task-name"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="fld-lbl">Start</label>
                  <input id="nt-start" type="date" className="fld" defaultValue={todayISO()}/>
                </div>
                <div>
                  <label className="fld-lbl">End</label>
                  <input id="nt-end" type="date" className="fld" defaultValue={todayISO()}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="fld-lbl">Progress %</label>
                  <input id="nt-pct" type="number" min={0} max={100} className="fld" defaultValue={0}/>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input id="nt-crit" type="checkbox"/>
                  <span>Critical path</span>
                </label>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[var(--border)] flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTask} data-testid="btn-save-task">Add Task</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Tile({ label, value, sub, color }) {
  return (
    <div className="card tile-accent p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-extrabold tracking-tighter mt-1" style={{ color }}>{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

// ─── Task Predecessor Picker (FS / SS / FF / SF + lag days) ─────────────────
function PredecessorPicker({ task, siblings, readOnly, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const preds = task.predecessors || [];

  // Build the set of task IDs the picker would create a cycle with — any
  // downstream descendant of the current task is unsafe as a predecessor.
  const descendants = useMemo(() => {
    const out = new Set();
    const walk = (id) => {
      for (const s of siblings) {
        if ((s.predecessors || []).some(p => p.id === id) && !out.has(s.id)) {
          out.add(s.id); walk(s.id);
        }
      }
    };
    walk(task.id);
    return out;
  }, [siblings, task.id]);

  const others = siblings.filter(x => x.id !== task.id && !descendants.has(x.id));

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const add = (id) => {
    if (preds.some(p => p.id === id)) return;
    onChange([...preds, { id, type: "FS", lag: 0 }]);
  };
  const removeAt = (idx) => {
    onChange(preds.filter((_, i) => i !== idx));
  };
  const updateAt = (idx, key, value) => {
    onChange(preds.map((p, i) => i === idx ? { ...p, [key]: value } : p));
  };

  return (
    <div ref={wrapRef} className="relative flex flex-wrap items-center gap-1">
      {preds.length === 0 && <span className="text-[10px] text-zinc-400 font-mono">—</span>}
      {preds.map((p, idx) => (
        <span key={`${p.id}-${idx}`}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-[var(--surface)] border border-[var(--border)] text-[10px] font-mono text-[var(--navy)]"
              title={`${DEP_LABELS[p.type] || p.type} · lag ${p.lag || 0}d`}
              data-testid={`pred-chip-${task.id}-${p.id}`}>
          <Link2 size={9}/>
          <span>{p.id}</span>
          {!readOnly ? (
            <>
              <select value={p.type || "FS"} onChange={e => updateAt(idx, "type", e.target.value)}
                      className="bg-transparent border-none text-[10px] font-mono px-0.5 focus:outline-none"
                      data-testid={`pred-type-${task.id}-${p.id}`}>
                {DEP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" value={p.lag || 0}
                     onChange={e => updateAt(idx, "lag", Number(e.target.value) || 0)}
                     className="bg-transparent border-none w-8 text-[10px] font-mono px-0.5 focus:outline-none"
                     title="Lag (days) — negative = lead"
                     data-testid={`pred-lag-${task.id}-${p.id}`}/>
              <button type="button" onClick={() => removeAt(idx)} className="ml-0.5 text-zinc-400 hover:text-red-600" title="Remove"
                      data-testid={`btn-pred-remove-${task.id}-${p.id}`}>
                <X size={9}/>
              </button>
            </>
          ) : (
            <span className="ml-0.5 text-zinc-500">·{p.type || "FS"}{p.lag ? ` ${p.lag > 0 ? "+" : ""}${p.lag}d` : ""}</span>
          )}
        </span>
      ))}
      {!readOnly && others.length > 0 && (
        <button type="button" className="text-[10px] font-mono text-zinc-500 hover:text-[var(--navy)] border border-dashed border-[var(--border)] rounded-sm px-1.5 py-0.5"
                onClick={() => setOpen(o => !o)} data-testid={`btn-pred-open-${task.id}`}>
          + link
        </button>
      )}
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 min-w-[220px] bg-white border border-[var(--border)] rounded-sm shadow-lg p-1.5" data-testid={`pred-popover-${task.id}`}>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-400 px-1.5 py-1">Add predecessor</div>
          <div className="max-h-56 overflow-y-auto">
            {others.filter(o => !preds.some(p => p.id === o.id)).map(o => (
              <button key={o.id} type="button"
                      className="w-full flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-[var(--surface)] cursor-pointer text-[11px] text-left"
                      onClick={() => { add(o.id); setOpen(false); }}
                      data-testid={`pred-add-${task.id}-${o.id}`}>
                <span className="font-mono text-[10px] text-zinc-500 w-10 shrink-0">{o.id}</span>
                <span className="truncate text-zinc-700">{o.name}</span>
              </button>
            ))}
            {others.filter(o => !preds.some(p => p.id === o.id)).length === 0 && (
              <div className="px-2 py-2 text-[10px] text-zinc-400 italic">All eligible tasks already linked.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Subtask dependency picker — chips + popover of siblings.
function DepsPicker({ sub, siblings, readOnly, onToggle }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const deps = sub.dependsOn || [];
  const others = siblings.filter(x => x.id !== sub.id);

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  // Show the chip label in the form "S2" when ID looks like "T1-S2_xx"
  const short = (id) => {
    const m = /-(S\d+)/.exec(id);
    return m ? m[1] : id.slice(-4);
  };

  return (
    <div ref={wrapRef} className="relative flex flex-wrap items-center gap-1">
      {deps.length === 0 && <span className="text-[10px] text-zinc-400 font-mono">—</span>}
      {deps.map(d => (
        <span key={d} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-[var(--surface)] border border-[var(--border)] text-[10px] font-mono text-[var(--navy)]" title={d}>
          <Link2 size={9}/> {short(d)}
          {!readOnly && (
            <button type="button" onClick={() => onToggle(d)} className="ml-0.5 text-zinc-400 hover:text-red-600" title="Remove dependency" data-testid={`btn-dep-remove-${sub.id}-${d}`}>
              <X size={9}/>
            </button>
          )}
        </span>
      ))}
      {!readOnly && others.length > 0 && (
        <button type="button" className="text-[10px] font-mono text-zinc-500 hover:text-[var(--navy)] border border-dashed border-[var(--border)] rounded-sm px-1.5 py-0.5"
                onClick={() => setOpen(o => !o)} data-testid={`btn-deps-open-${sub.id}`}>
          + add
        </button>
      )}
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 min-w-[180px] bg-white border border-[var(--border)] rounded-sm shadow-lg p-1.5" data-testid={`deps-popover-${sub.id}`}>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-400 px-1.5 py-1">Pick prerequisites</div>
          <div className="max-h-48 overflow-y-auto">
            {others.map(o => {
              const on = deps.includes(o.id);
              return (
                <label key={o.id} className="flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-[var(--surface)] cursor-pointer text-[11px]">
                  <input type="checkbox" checked={on} onChange={() => onToggle(o.id)} data-testid={`dep-check-${sub.id}-${o.id}`}/>
                  <span className="font-mono text-[10px] text-zinc-500 w-12 shrink-0">{short(o.id)}</span>
                  <span className="truncate text-zinc-700">{o.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}